import { IHistory, InputFieldConfig } from '../../../../../types';
import { StudentState } from '../../../../../store/student/types';
import { CourseLevel } from 'types';

type Fields = Pick<
  IHistory,
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'previousUkStudyVisas'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason'
>;

type Visible = Record<
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason',
  boolean
>;

type Mandatory = Record<
  | 'lastCourse'
  | 'lastCourseOther'
  | 'lastCourseYear'
  | 'hasOverseasTravel'
  | 'overseasTravelTimes'
  | 'hasUkTravel'
  | 'ukTravelTimes'
  | 'hasPreviousUkStudy'
  | 'hasPreviousUkStudyVisa'
  | 'lastCourseLevel'
  | 'lowerOrSameLevelReason',
  boolean
>;

export function getPageStatus(
  config: StudentState['config'],
  fields: Fields,
): {
  complete: boolean;
  visible: Visible;
} {
  const {
    lastCourse,
    lastCourseOther,
    lastCourseYear,
    hasOverseasTravel,
    overseasTravelTimes,
    hasUkTravel,
    ukTravelTimes,
    hasPreviousUkStudy,
    hasPreviousUkStudyVisa,
    previousUkStudyVisas,
    lastCourseLevel,
    lowerOrSameLevelReason,
  } = fields;

  const {
    LastCourse: lastCourseConfig,
    LastCourseOther: lastCourseOtherConfig,
    LastCourseYear: lastCourseYearConfig,
    HasOverseasTravel: hasOverseasTravelConfig,
    OverseasTravelTimes: overseasTravelTimesConfig,
    HasUKTravel: hasUkTravelConfig,
    UKTravelTimes: ukTravelTimesConfig,
    HasPreviousUkStudy: hasPreviousUkStudyConfig,
    HasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig,
    LastCourseLevel: lastCourseLevelConfig,
    LowerOrSameLevelReason: lowerOrSameLevelReasonConfig,
  }: {
    LastCourse: InputFieldConfig;
    LastCourseOther: InputFieldConfig;
    LastCourseYear: InputFieldConfig;
    HasOverseasTravel: InputFieldConfig;
    OverseasTravelTimes: InputFieldConfig;
    HasUKTravel: InputFieldConfig;
    UKTravelTimes: InputFieldConfig;
    HasPreviousUkStudy: InputFieldConfig;
    HasPreviousUkStudyVisa: InputFieldConfig;
    LastCourseLevel: InputFieldConfig;
    LowerOrSameLevelReason: InputFieldConfig;
  } = config.Checklist.History.Page1;

  const visible: Visible = {
    lastCourse: lastCourseConfig?.visible ?? true,
    lastCourseOther: (lastCourseOtherConfig?.visible ?? true) && lastCourse === 'Other',
    lastCourseYear: lastCourseYearConfig?.visible ?? true,
    hasOverseasTravel: hasOverseasTravelConfig?.visible ?? true,
    overseasTravelTimes: (overseasTravelTimesConfig?.visible ?? true) && hasOverseasTravel === true,
    hasUkTravel: (hasUkTravelConfig?.visible ?? true) && hasOverseasTravel === true,
    ukTravelTimes: (ukTravelTimesConfig?.visible ?? true) && hasOverseasTravel === true && hasUkTravel === true,
    hasPreviousUkStudy: hasPreviousUkStudyConfig?.visible ?? true,
    hasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig?.visible ?? true,
    lastCourseLevel: (lastCourseLevelConfig?.visible ?? true) && hasPreviousUkStudyVisa === true,
    lowerOrSameLevelReason:
      (lastCourseLevelConfig?.visible ?? true) &&
      (lowerOrSameLevelReasonConfig?.visible ?? true) &&
      [CourseLevel.SAME, CourseLevel.LOWER].includes(lastCourseLevel!),
  };

  const mandatory: Mandatory = {
    lastCourse: lastCourseConfig?.mandatory ?? true,
    lastCourseOther: lastCourseOtherConfig?.mandatory ?? true,
    lastCourseYear: lastCourseYearConfig?.mandatory ?? true,
    hasOverseasTravel: hasOverseasTravelConfig?.mandatory ?? true,
    overseasTravelTimes: overseasTravelTimesConfig?.mandatory ?? true,
    hasUkTravel: hasUkTravelConfig?.mandatory ?? true,
    ukTravelTimes: ukTravelTimesConfig?.mandatory ?? true,
    hasPreviousUkStudy: hasPreviousUkStudyConfig?.mandatory ?? true,
    hasPreviousUkStudyVisa: hasPreviousUkStudyVisaConfig?.mandatory ?? true,
    lastCourseLevel: lastCourseLevelConfig?.mandatory ?? true,
    lowerOrSameLevelReason: lowerOrSameLevelReasonConfig?.mandatory ?? true,
  };

  const allVisasComplete = (amount: number): boolean => {
    let complete = (hasPreviousUkStudyVisa === true && amount > 0) || hasPreviousUkStudyVisa === false;
    for (let i = 0; i < amount; i++) {
      complete =
        complete &&
        !!previousUkStudyVisas![i] &&
        previousUkStudyVisas![i].visaType !== '' &&
        previousUkStudyVisas![i].level !== '' &&
        previousUkStudyVisas![i].year !== '' &&
        previousUkStudyVisas![i].isComplete !== undefined &&
        (previousUkStudyVisas![i].isComplete === false
          ? previousUkStudyVisas![i].incompleteReason !== '' &&
            previousUkStudyVisas![i].isIncompleteRecorded !== undefined &&
            previousUkStudyVisas![i].isVisaShortened !== undefined &&
            (previousUkStudyVisas![i].isVisaShortened === true ? previousUkStudyVisas![i].shortenedDate !== '' : true)
          : true);
    }
    return complete;
  };

  // prettier-ignore
  const complete = (!visible.lastCourse || !mandatory.lastCourse || lastCourse !== '') &&
    (!visible.lastCourseOther || !mandatory.lastCourseOther || lastCourseOther !== '') &&
    (!visible.lastCourseYear || !mandatory.lastCourseYear || lastCourseYear !== '') &&
    (!visible.hasOverseasTravel || !mandatory.hasOverseasTravel || hasOverseasTravel !== undefined) &&
    (!visible.overseasTravelTimes || !mandatory.overseasTravelTimes || overseasTravelTimes !== '') &&
    (!visible.hasUkTravel || !mandatory.hasUkTravel || hasUkTravel !== undefined) &&
    (!visible.ukTravelTimes || !mandatory.ukTravelTimes || ukTravelTimes !== '') &&
    ((!visible.hasPreviousUkStudy || !mandatory.hasPreviousUkStudy || hasPreviousUkStudy === false) ||
      (hasPreviousUkStudy === true && (!visible.hasPreviousUkStudyVisa || !mandatory.hasPreviousUkStudyVisa || hasPreviousUkStudyVisa !== undefined) &&
          (!visible.lastCourseLevel || !mandatory.lastCourseLevel || lastCourseLevel !== undefined) &&
          (!visible.lowerOrSameLevelReason || !mandatory.lowerOrSameLevelReason || (lowerOrSameLevelReason !== undefined && lowerOrSameLevelReason !== '')) &&
        allVisasComplete(Number(previousUkStudyVisas!.length))));

  return {
    complete,
    visible,
  };
}

export const getCourseLevel = (level: CourseLevel) => {
  const levelName = level.toLowerCase();
  return level === CourseLevel.NO_UK_STUDY ? 'No UK Study' : levelName.charAt(0).toUpperCase() + levelName.slice(1);
};
